import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
    Button, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Typography
} from '@mui/material'
import { ExclamationCircleOutlined } from '@ant-design/icons'

import { useSnackbar } from 'notistack'
import AnimateButton from 'components/@extended/AnimateButton'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { EVENT_BANNER_DELETE_INIT, EVENT_BANNER_UPDATE_SUCCESS } from 'models/events/banner.event'
import { bannerDelete } from 'models/banner'

export const BannerDeleteDialog = () => {
    const [banner, setBanner] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        // payload is the row item, e.g. { id, name, ... }
        return eventbus.on(EVENT_BANNER_DELETE_INIT, (data) => setBanner(data))
    }, [eventbus])

    return (
        <Dialog open={banner != null} maxWidth="xs" fullWidth onClose={() => setBanner(null)}>
            {banner && (
                <BannerDelete
                    banner={banner}
                    onClose={() => setBanner(null)}
                    onSuccess={() => {
                        eventbus.fire(EVENT_BANNER_UPDATE_SUCCESS)
                        setBanner(null)
                    }}
                />
            )}
        </Dialog>
    )
}

const BannerDelete = ({ banner, onClose, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar()
    const [submitting, setSubmitting] = useState(false)

    const handleDelete = async () => {
        setSubmitting(true)
        await bannerDelete(banner.id)
            .then((res) => {
                onSuccess()
                enqueueSnackbar(res.message || 'Banner deleted successfully', { variant: 'success', autoHideDuration: 1500 })
            })
            .catch((res) => {
                enqueueSnackbar(res.message || 'Failed to delete banner', { variant: 'error', autoHideDuration: 1500 })
            })
            .finally(() => setSubmitting(false))
    }

    return (
        <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                Delete Banner
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete{' '}
                    <Typography component="span" fontWeight={600}>{banner.name}</Typography>?
                    This will also delete all of its slides. This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 1.5 }}>
                <AnimateButton>
                    <Button disabled={submitting} onClick={onClose} variant="outlined" color="secondary">
                        Cancel
                    </Button>
                </AnimateButton>
                <AnimateButton>
                    <Button disabled={submitting} onClick={handleDelete} variant="contained" color="error">
                        {submitting ? 'Deleting...' : 'Delete'}
                    </Button>
                </AnimateButton>
            </DialogActions>
        </>
    )
}

BannerDelete.propTypes = {
    banner: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    onSuccess: PropTypes.func
}

export default BannerDelete